// What a customer reads from a department (BL-052), as Settings › Departments & job types edits it:
// the company's own values, and for a department each field either "Same as the company" or its
// own. These read settings.list's rows and say which settings.set / settings.clear calls a change
// is; the server checks every value again.

import {
  fillParagraph,
  PARAGRAPH_FIELDS,
  unknownParagraphFields,
  type ParagraphKind,
} from '@flowology/domain';
import type { SettingRow } from './settings-model.ts';

export interface TextField {
  key: string;
  label: string;
  kind: 'line' | 'lines' | 'paragraph';
  /** For a signed paragraph, which one (its merge fields). */
  paragraph?: ParagraphKind;
  help?: string;
  placeholder?: string;
}

export const CUSTOMER_TEXT_FIELDS: readonly TextField[] = [
  {
    key: 'company.officialName',
    label: 'Official name',
    kind: 'line',
    help: 'Printed on estimates and invoices and signed in messages. Blank: the company’s name.',
  },
  { key: 'company.phone', label: 'Phone', kind: 'line', placeholder: '(250) 555-0100' },
  { key: 'company.email', label: 'Email', kind: 'line', placeholder: 'office@example.com' },
  { key: 'company.addressLines', label: 'Address', kind: 'lines', help: 'One line each, up to five.' },
  { key: 'company.licenceNumber', label: 'Licence number', kind: 'line' },
  {
    key: 'documents.invoiceHeader',
    label: 'Invoice header',
    kind: 'paragraph',
    help: 'At the top of every invoice.',
  },
  {
    key: 'documents.invoiceFooter',
    label: 'Invoice footer',
    kind: 'paragraph',
    help: 'At the foot of every invoice: a thank-you, warranty words, how to pay.',
  },
  {
    key: 'estimates.termsText',
    label: 'Authorisation (signed to approve the work)',
    kind: 'paragraph',
    paragraph: 'authorisation',
  },
  {
    key: 'invoices.acknowledgementText',
    label: 'Acknowledgement (signed to accept the finished work)',
    kind: 'paragraph',
    paragraph: 'acknowledgement',
  },
];

export const CUSTOMER_TEXT_KEYS = new Set(CUSTOMER_TEXT_FIELDS.map((f) => f.key));

/** One field on the form: the text, and for a department whether it's its own. */
export interface TextValue {
  own: boolean;
  text: string;
  /** The company's value, shown when the department uses it. */
  company: string;
}
export type TextForm = Record<string, TextValue>;

/** A stored value as the text field shows it (address lines one per line; none is blank). */
function asText(f: TextField, v: unknown): string {
  if (v === null || v === undefined) return '';
  if (f.kind === 'lines') return Array.isArray(v) ? v.join('\n') : '';
  return String(v);
}

/** The text as the setting stores it: lines as a list, blank as nothing (null, or no lines). */
function asValue(f: TextField, text: string): unknown {
  if (f.kind === 'lines')
    return text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
  return text.trim() || null;
}

/** The form for the company (unitId null) or one department, from settings.list's rows. */
export function textForm(rows: readonly SettingRow[], unitId: string | null): TextForm {
  const form: TextForm = {};
  for (const f of CUSTOMER_TEXT_FIELDS) {
    const row = rows.find((r) => r.key === f.key);
    const company = asText(f, row?.value);
    const o = unitId
      ? row?.overrides.find((x) => x.scopeType === 'business_unit' && x.scopeId === unitId)
      : undefined;
    form[f.key] = { own: !!o, text: o ? asText(f, o.value) : company, company };
  }
  return form;
}

export type TextWrite =
  | {
      action: 'settings.set';
      input: { key: string; value: unknown; scopeType?: 'business_unit'; scopeId?: string };
    }
  | { action: 'settings.clear'; input: { key: string; scopeType: 'business_unit'; scopeId: string } };

/**
 * The writes a change is, in field order: for the company, each value that changed; for a
 * department, a value of its own set, or one given back to the company cleared. A paragraph with a
 * field it can't use, or the company's authorisation or acknowledgement left empty, is refused first.
 */
export function textWrites(
  was: TextForm,
  now: TextForm,
  unitId: string | null,
): { writes: TextWrite[] } | { error: string } {
  const writes: TextWrite[] = [];
  for (const f of CUSTOMER_TEXT_FIELDS) {
    const a = was[f.key]!;
    const b = now[f.key]!;
    if (f.paragraph && (b.own || !unitId)) {
      if (!b.text.trim()) return { error: `${f.label}: write the paragraph customers sign.` };
      const problem = paragraphProblem(b.text, f.paragraph);
      if (problem) return { error: `${f.label}: ${problem}` };
    }
    if (!unitId) {
      if (a.text !== b.text)
        writes.push({ action: 'settings.set', input: { key: f.key, value: asValue(f, b.text) } });
    } else if (a.own && !b.own) {
      writes.push({
        action: 'settings.clear',
        input: { key: f.key, scopeType: 'business_unit', scopeId: unitId },
      });
    } else if (b.own && (!a.own || a.text !== b.text)) {
      writes.push({
        action: 'settings.set',
        input: { key: f.key, value: asValue(f, b.text), scopeType: 'business_unit', scopeId: unitId },
      });
    }
  }
  return { writes };
}

/** What's wrong with a paragraph's fields, in the server's words, or null. */
export function paragraphProblem(text: string, kind: ParagraphKind): string | null {
  const unknown = unknownParagraphFields(text, kind);
  if (!unknown.length) return null;
  const known = PARAGRAPH_FIELDS[kind].map(([k]) => `{${k}}`).join(', ');
  return `${unknown.map((k) => `{${k}}`).join(' and ')} ${unknown.length === 1 ? 'isn’t a field' : 'aren’t fields'} this paragraph can use. It can use ${known}.`;
}

/** A paragraph as a customer would read it, filled from a made-up job. */
export function paragraphPreview(text: string, company: string): string {
  return fillParagraph(
    text,
    {
      Company: company,
      Customer: 'Okafor family',
      Total: '$1,189.00',
      AuthorizedAmount: '$1,189.00',
      Terms: 'Net 30',
      EstimateNumber: '1042',
      InvoiceNumber: '24581',
    },
    true,
  );
}
